import LinkWithQuery from "@src/straps/base/navigation/LinkWithQuery/LinkWithQuery";
import { useCurrentAccount } from "@src/backpack-console/CurrentAccountManager";
import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import {
  useDebounce,
  useDidClickOutsideElement,
} from "@src/global_functions/hooks";
import { getImageUrlFromKey } from "@src/global_functions/postgrestApi";
import BuildingImage from "@src/straps/atoms/BuildingImage/BuildingImage";
import WarningMessage from "@src/straps/derived/WarningMessage/WarningMessage";
import { ClickableDiv } from "@src/straps/utils/ClickableDiv";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import {
  createRef,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Icon from "../../icons/Icon/Icon";
import Skeleton from "../../skeleton/Skeleton";
import Text from "../../type/Text/Text";
import { SelectedSite } from "./BuildingHeader";
import styles from "./BuildingSearch.module.scss";
import { HighlightMatch } from "./HighlightMatch";
import { useGetPathnameToSite } from "./useGetPathnameToSite";

const TOP_NAV_HEIGHT_CLASS = "h-[70px]";

function useSiteFromSearch(searchTerm: string, idx: number) {
  const { accountId } = useCurrentAccount();
  const { data, isLoading } = backpackSdk.sites.search.useQuery({
    query: searchTerm,
    account_id: accountId,
    page: Math.floor(idx / SITE_SEARCH_PAGE_SIZE) + 1,
    page_size: SITE_SEARCH_PAGE_SIZE,
  });
  const site = data?.rows[idx % SITE_SEARCH_PAGE_SIZE];
  return { site, isLoading };
}

const BUILDING_ROW_HEIGHT = 73;
const SITE_SEARCH_PAGE_SIZE = 20;
function BuildingItem({
  searchTerm,
  idx,
  getLink,
  onClose,
}: Readonly<{
  searchTerm: string;
  idx: number;
  getLink: (siteId: number) => string;
  onClose: () => void;
}>) {
  const { accountId } = useCurrentAccount();
  const { site, isLoading } = useSiteFromSearch(searchTerm, idx);

  const randomLoadingWidth = useMemo(() => {
    return 200 + Math.random() * 200;
  }, []);

  if (isLoading || !site) {
    return (
      <div
        style={{ height: BUILDING_ROW_HEIGHT }}
        className="flex w-full items-center gap-3 border-b border-straps-tertiary/25"
      >
        <BuildingImage name="loading" />
        <Skeleton className="h-6" style={{ width: randomLoadingWidth }} />
      </div>
    );
  }

  return (
    <LinkWithQuery
      to={getLink(site.site_id)}
      onClick={onClose}
      className="group flex cursor-pointer items-center gap-3 border-b border-straps-tertiary/25 text-straps-primary transition hover:bg-straps-body"
    >
      <BuildingImage
        imageURL={getImageUrlFromKey(site.image_key)}
        name={site.name ?? "No Building Name"}
      />
      <div className="flex flex-col">
        <Text
          as="span"
          variant="bc_s-16-500"
          className="group-hover:text-straps-hyperlink-hover"
          color={site.account_id === accountId ? "primary" : "secondary"}
        >
          <HighlightMatch text={site.name ?? ""} query={searchTerm} />
          {site.status === "inactive" && (
            <>
              {" "}
              <span className="text-straps-secondary">(inactive)</span>
            </>
          )}
        </Text>
        {site.ubin_property_code && (
          <Text
            as="span"
            variant="sb_t-10-500"
            color="secondary"
            className="group-hover:text-straps-hyperlink-hover"
          >
            <HighlightMatch text={site.ubin_property_code} query={searchTerm} />
          </Text>
        )}
      </div>
    </LinkWithQuery>
  );
}

const LOADING_SKELETON_COUNT = 4;
function BuildingList({
  isOpen,
  searchTerm,
  onClose,
  count,
  isLoading,
  hideBorderTop,
}: Readonly<{
  isOpen: boolean;
  searchTerm: string;
  onClose: () => void;
  count: number;
  isLoading?: boolean;
  hideBorderTop?: boolean;
}>) {
  const parentRef = createRef<HTMLDivElement>();
  const virtualizer = useVirtualizer({
    count: isLoading ? LOADING_SKELETON_COUNT : count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => BUILDING_ROW_HEIGHT,
    overscan: 5,
  });
  const getLink = useGetPathnameToSite();

  // Reset scroll when buildings change or dropdown is closed
  useEffect(() => {
    if (parentRef.current) {
      parentRef.current.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isOpen]);

  return (
    <div
      data-testid="top-nav-building-selector-building-list"
      ref={parentRef}
      className={classNames(
        styles.snap_container,
        "overflow-y-auto p-4 transition-all",
        !hideBorderTop && "border-t border-straps-tertiary/25"
      )}
    >
      {!isLoading && count === 0 ? (
        <WarningMessage message="No properties found" height={150} />
      ) : (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            return (
              <div
                key={virtualRow.index}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  height: BUILDING_ROW_HEIGHT,
                }}
              >
                <BuildingItem
                  searchTerm={searchTerm}
                  getLink={getLink}
                  idx={virtualRow.index}
                  onClose={onClose}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ImprovedNavHeader({
  isMounted,
  isOpen,
  selected,
  inputRef,
  searchTerm,
  onSearchTermChange,
  onHeaderClick,
}: Readonly<{
  isMounted: boolean;
  isOpen: boolean;
  selected?: SelectedSite | null;
  inputRef: RefObject<HTMLInputElement>;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onHeaderClick: () => void;
}>) {
  let contentSlot: ReactNode;
  if (isMounted) {
    contentSlot = (
      <input
        data-testid="top-nav-building-selector-search-input"
        ref={inputRef}
        className="z-1 flex-1 border-0 text-bn font-medium outline-none placeholder:text-sb_t-16-500 placeholder:text-straps-tertiary"
        placeholder="Search Properties..."
        value={searchTerm}
        onChange={(e) => onSearchTermChange(e.target.value)}
        maxLength={256}
      />
    );
  } else if (selected?.name) {
    contentSlot = (
      <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <span className="truncate text-sm font-bold text-straps-primary xl:text-base">
          {selected.name}
          {selected.status === "inactive" && (
            <span className="text-straps-secondary"> (inactive)</span>
          )}
        </span>
        {selected.ubinPropertyCode && (
          <>
            <span className="h-4 w-px shrink-0 bg-straps-tertiary/25" />
            <Text
              as="span"
              variant="sb_t-10-500"
              color="secondary"
              className="shrink-0 leading-none"
            >
              {selected.ubinPropertyCode}
            </Text>
          </>
        )}
      </span>
    );
  } else {
    contentSlot = (
      <Text
        as="span"
        variant="sb_t-16-500"
        color="tertiary"
        className="ml-0.5 shrink-0 transition group-hover:text-straps-primary"
      >
        Search Properties...
      </Text>
    );
  }

  return (
    <div
      data-testid={!isMounted ? "top-nav-building-selector-button" : undefined}
      className="group flex h-11 cursor-default items-center gap-2.5 px-4"
      onClick={!isMounted ? onHeaderClick : undefined}
      onKeyDown={
        !isMounted
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onHeaderClick();
            }
          : undefined
      }
    >
      <Icon
        name="search"
        size="medium"
        className={classNames("z-1 h-3 w-3 shrink-0 transition", {
          "text-straps-primary": isMounted,
          "text-straps-secondary": !isMounted,
        })}
      />
      {contentSlot}
      <ClickableDiv
        aria-label={isOpen ? "Collapse search" : "Expand search"}
        onClick={(e) => {
          e.stopPropagation();
          onHeaderClick();
        }}
        className="ml-auto flex shrink-0 cursor-pointer items-center"
      >
        <Icon
          name="caret-up"
          size="small"
          className={classNames(
            "transition-all group-hover:text-straps-primary",
            {
              "rotate-180 text-straps-secondary": !isOpen,
              "text-straps-primary": isOpen,
            }
          )}
        />
      </ClickableDiv>
    </div>
  );
}

export const BuildingSearch = ({
  selected,
}: {
  readonly selected?: SelectedSite | null;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useDidClickOutsideElement(() => setIsOpen(false));
  const inputRef = createRef<HTMLInputElement>();
  const debouncedSearchTerm = useDebounce(searchTerm, 250);
  const { accountId } = useCurrentAccount();

  const { data, isLoading, isError } = backpackSdk.sites.search.useQuery({
    query: debouncedSearchTerm,
    account_id: accountId,
    page: 1,
    page_size: SITE_SEARCH_PAGE_SIZE,
  });

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isMounted) {
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [isMounted]);

  useEffect(() => {
    if (isVisible && inputRef.current) inputRef.current.focus();
  }, [isVisible, inputRef]);

  const onHeaderClick = useCallback(() => {
    setIsOpen((prev) => !prev);
    setSearchTerm("");
  }, []);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
  }, []);

  const handleTransitionEnd = useCallback(() => {
    if (!isOpen) setIsMounted(false);
  }, [isOpen]);

  return (
    <div
      data-testid="top-nav-building-selector"
      ref={containerRef}
      className={classNames(
        "relative z-10 flex min-w-[180px] flex-1 items-center px-2",
        TOP_NAV_HEIGHT_CLASS
      )}
    >
      <div
        className={classNames(
          "border-straps-tertiary/15 absolute left-2 right-2 top-[13px] z-10 overflow-hidden rounded-[22px] border bg-pure-white transition-[box-shadow]",
          {
            "shadow-lg": isVisible,
            "shadow-sm": !isVisible,
          }
        )}
      >
        <ImprovedNavHeader
          isMounted={isMounted}
          isOpen={isOpen}
          selected={selected}
          inputRef={inputRef}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onHeaderClick={onHeaderClick}
        />
        {isMounted && (
          <div
            data-testid="top-nav-building-selector-search-box"
            onTransitionEnd={handleTransitionEnd}
            className={classNames(
              "flex flex-col overflow-hidden transition-all",
              styles.outer_container,
              { [styles.closed!]: !isVisible }
            )}
          >
            {isError ? (
              <WarningMessage message="Something went wrong" />
            ) : (
              <BuildingList
                searchTerm={debouncedSearchTerm}
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading}
                count={data?.pagination.total ?? 0}
                hideBorderTop={true}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
